import React, { useEffect, useRef, useState } from 'react'
import { MAX_OTP_LENGTH, OTP_PURPOSE_SIGNUP, OTP_TIMER_SECONDS } from '../../../constants/otp'
import moment from 'moment'
import { handlePostRequest } from '../../../utils/api-handler.util'
import { showToaster } from '../../../utils/show-toaster.util'
import { useMutation } from '@tanstack/react-query'
import OTPInput from '../../../components/auth/OTPInput'

export default function StepOtpVerification({ data, onNext }) {
  const phoneNumber = data?.phone

  const [otp, setOtp] = useState(''.padEnd(MAX_OTP_LENGTH, ''))
  const timerEnd = useRef(moment().add(OTP_TIMER_SECONDS, 'seconds'))
  const [secondsLeft, setSecondsLeft] = useState(OTP_TIMER_SECONDS)
  const [error, setError] = useState()

  const sendOtpMutation = useMutation({
    mutationFn: () =>
      handlePostRequest('/otp/generate', {
        phoneNumber,
        purpose: OTP_PURPOSE_SIGNUP,
      }),
    onSuccess: () => {
      showToaster('success', 'The OTP has been sent to your phone number.')
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.errorMessage
      if (error?.response?.data?.code === 'OTP_RESEND_LIMIT') {
        setError('You have reached the maximum number of resend attempts')
      } else {
        setError(errorMessage)
      }
    },
  })

  const verifyOtpMutation = useMutation({
    mutationFn: async ({ phoneNumber, code }) => {
      return await handlePostRequest('/otp/verify', {
        phoneNumber,
        purpose: OTP_PURPOSE_SIGNUP,
        otpCode: code,
      })
    },
    onSuccess: (data) => {
      showToaster('success', 'OTP verified!')
      const otpDetails = {
        otp: {
          id: data.data.otp.id,
          otpCode: data.data.otp.otpCode,
          phoneNumber: data.data.otp.phoneNumber,
          isConfirmed: data.data.otp.isConfirmed,
          confirmedAt: data.data.otp.confirmedAt,
        },
      }

      onNext({
        otpDetails,
      })
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.errorMessage || 'An unknown error occurred.'
      throw new Error(errorMessage)
    },
  })

  const hasSentOtp = useRef(false)

  useEffect(() => {
    if (phoneNumber && !hasSentOtp.current) {
      sendOtpMutation.mutate()
      hasSentOtp.current = true
    }
  }, [phoneNumber, sendOtpMutation])

  useEffect(() => {
    const interval = setInterval(() => {
      const diff = timerEnd.current.diff(moment(), 'seconds')
      setSecondsLeft(diff > 0 ? diff : 0)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const handleOtpChange = (otpString) => {
    setOtp(otpString)
  }

  const handleOtpComplete = (otpString) => {
    if (otpString.length !== MAX_OTP_LENGTH) {
      setError(`Please enter the ${MAX_OTP_LENGTH}-digit code.`)
      return
    }
    verifyOtpMutation.mutate({ phoneNumber, code: otpString })
  }

  const handleResend = () => {
    if (secondsLeft === 0 && phoneNumber) {
      sendOtpMutation.mutate()
      timerEnd.current = moment().add(OTP_TIMER_SECONDS, 'seconds')
    }
  }

  const formatTimer = () => {
    const m = String(Math.floor(secondsLeft / 60)).padStart(2, '0')
    const s = String(secondsLeft % 60).padStart(2, '0')
    return `${m}:${s}`
  }

  return (
    <div className="flex flex-col items-center gap-5 ">
      <h1
        className="text-[25.5px] font-bold text-black mb-1 font-inter text-center"
        style={{ lineHeight: '31px' }}
      >
        OTP VERIFICATION
      </h1>
      <p
        className="text-[17px] font-medium text-black/50 mb-8 font-inter text-center"
        style={{ lineHeight: '21px' }}
      >
        Enter The Four Digit Code
      </p>
      <div className="flex justify-center gap-6 mb-6 w-full">
        <OTPInput
          length={MAX_OTP_LENGTH}
          onChange={handleOtpChange}
          onComplete={handleOtpComplete}
        />
      </div>
      {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
      <button
        className="w-[324px] h-[55px] bg-gradient-to-r from-[#DE8516] to-[#F08A0A] text-white font-medium text-[18px] rounded-[10px] shadow-md mb-6"
        onClick={() => handleOtpComplete(otp)}
        disabled={verifyOtpMutation.isPending}
      >
        {verifyOtpMutation.isPending ? 'Verifying...' : 'VERIFY'}
      </button>
      <div className="text-[18px] font-normal text-black/60 text-center font-inter">
        Resend:{' '}
        <button
          className="text-[#055299] font-bold"
          onClick={handleResend}
          disabled={secondsLeft > 0 || sendOtpMutation.isPending}
        >
          {formatTimer()}
        </button>
      </div>
    </div>
  )
}
